import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select } from 'antd';
import {
  useGetClientTeamQuery,
  useGetClientRolesQuery,
} from '../../features/platform/platformApi';
import { useDeleteUserMutation } from '../../features/users/usersApi';

const desigText = (d: string | string[] | null) =>
  Array.isArray(d) ? d.filter(Boolean).join(', ') : d || '';

/* ------------------------------------------------------------------ *
 * Client Admin — Team and access (prototype `isTeam`).
 * Roles (permissions), members (role + area + request types), and a
 * "who handles a request?" explorer that shows the government escalation
 * chain beside the client's own team. Mock.
 * ------------------------------------------------------------------ */

const CATS = ['Land & revenue', 'Police & law and order', 'Health', 'Water', 'Electricity', 'Roads', 'Party work'];
const SCOPE_OPTS = [
  { value: 'all', label: 'Whole boundary' },
  { value: 'd:Alwar', label: 'Alwar district' },
  { value: 't:Reni', label: 'Reni tehsil' },
  { value: 't:Rajgarh', label: 'Rajgarh tehsil' },
];
const VILLAGES = [
  { value: 'karanpura', label: 'Karanpura · Reni tehsil', tehsil: 'Reni', district: 'Alwar' },
  { value: 'bahadarpur', label: 'Bahadarpur · Rajgarh tehsil', tehsil: 'Rajgarh', district: 'Alwar' },
  { value: 'naugaon', label: 'Naugaon · Ramgarh tehsil', tehsil: 'Ramgarh', district: 'Alwar' },
];

interface Member {
  id: string;
  name: string;
  role: string;
  scope: string;
  handles: string[];
}
const INITIAL_MEMBERS: Member[] = [
  { id: 'm1', name: '[Office admin]', role: 'Admin', scope: 'all', handles: [...CATS] },
  { id: 'm2', name: '[Coordinator, Alwar]', role: 'Coordinator', scope: 'd:Alwar', handles: ['Land & revenue', 'Police & law and order'] },
  { id: 'm3', name: '[Field worker, Reni]', role: 'Field worker', scope: 't:Reni', handles: ['Land & revenue'] },
  { id: 'm4', name: '[Field worker, Rajgarh]', role: 'Field worker', scope: 't:Rajgarh', handles: ['Police & law and order', 'Land & revenue'] },
];

// Government escalation chain per request type (mock). `status`:
//   'review' → submitted, waiting for review (a gap);  'gap' → no one on record.
interface Step {
  tier: string;
  title: string;
  place: string;
  person?: string;
  meta?: string;
  status?: 'review' | 'gap';
}
const CHAINS: Record<string, { dept: string; steps: Step[] }> = {
  'Police & law and order': {
    dept: 'Police',
    steps: [
      { tier: 'Follows up', title: 'Station House Officer', place: 'Raini', status: 'review' },
      { tier: 'Escalation 1', title: 'Circle Officer (DSP)', place: 'Rajgarh', status: 'gap' },
      { tier: 'Escalation 2', title: 'Superintendent of Police', place: 'Alwar', person: 'Sudhir Chaudhary', meta: 'Police research page · since Jul 2025' },
      { tier: 'Escalation 3', title: 'Inspector General', place: 'Jaipur Range', person: 'Rahul Prakash', meta: 'Police research page · since Jul 2025' },
      { tier: 'Escalation 4', title: 'Director General of Police', place: 'Rajasthan', person: 'Rajeev Kumar Sharma', meta: 'Police research page · 2026' },
      { tier: 'Minister', title: 'Chief Minister (Police)', place: 'Rajasthan', person: '[Chief Minister]', meta: 'elected · political escalation' },
    ],
  },
  'Land & revenue': {
    dept: 'Revenue',
    steps: [
      { tier: 'Follows up', title: 'Patwari', place: 'Karanpura', status: 'review' },
      { tier: 'Escalation 1', title: 'Tehsildar', place: 'Reni', status: 'gap' },
      { tier: 'Escalation 2', title: 'Sub-Divisional Magistrate', place: 'Rajgarh', person: 'Meena Kumari', meta: 'Revenue research page · since 2024' },
      { tier: 'Escalation 3', title: 'District Collector', place: 'Alwar', person: 'Jitendra Kumar Soni', meta: 'Revenue research page · since Aug 2025' },
      { tier: 'Escalation 4', title: 'Divisional Commissioner', place: 'Jaipur', person: 'Puneet Mathur', meta: 'Revenue research page · 2026' },
      { tier: 'Minister', title: 'Chief Minister (Revenue)', place: 'Rajasthan', person: '[Chief Minister]', meta: 'elected · political escalation' },
    ],
  },
  Health: {
    dept: 'Health',
    steps: [
      { tier: 'Follows up', title: 'Medical Officer', place: 'Reni CHC', status: 'review' },
      { tier: 'Escalation 1', title: 'Block CMO', place: 'Rajgarh', status: 'gap' },
      { tier: 'Escalation 2', title: 'Chief Medical & Health Officer', place: 'Alwar', person: 'Dr. Suresh Yadav', meta: 'Health research page · since 2024' },
      { tier: 'Escalation 3', title: 'Director, Medical & Health', place: 'Rajasthan', person: 'Dr. Ravi Prakash', meta: 'Health research page · 2025' },
      { tier: 'Escalation 4', title: 'Principal Secretary, Health', place: 'Rajasthan', person: 'Gayatri Rathore', meta: 'Health research page · 2026' },
      { tier: 'Minister', title: 'Chief Minister (Health)', place: 'Rajasthan', person: '[Chief Minister]', meta: 'elected · political escalation' },
    ],
  },
  Water: {
    dept: 'Water (PHED)',
    steps: [
      { tier: 'Follows up', title: 'Junior Engineer (PHED)', place: 'Reni', status: 'review' },
      { tier: 'Escalation 1', title: 'Assistant Engineer', place: 'Rajgarh', status: 'gap' },
      { tier: 'Escalation 2', title: 'Executive Engineer (PHED)', place: 'Alwar', person: 'Mahesh Gupta', meta: 'Water research page · since 2024' },
      { tier: 'Escalation 3', title: 'Superintending Engineer', place: 'Jaipur', person: 'Anil Saxena', meta: 'Water research page · 2025' },
      { tier: 'Escalation 4', title: 'Chief Engineer (PHED)', place: 'Rajasthan', person: 'R. K. Meena', meta: 'Water research page · 2026' },
      { tier: 'Minister', title: 'Chief Minister (PHED)', place: 'Rajasthan', person: '[Chief Minister]', meta: 'elected · political escalation' },
    ],
  },
  Electricity: {
    dept: 'Electricity (JVVNL)',
    steps: [
      { tier: 'Follows up', title: 'Junior Engineer (JVVNL)', place: 'Reni', status: 'review' },
      { tier: 'Escalation 1', title: 'Assistant Engineer', place: 'Rajgarh', status: 'gap' },
      { tier: 'Escalation 2', title: 'Executive Engineer (JVVNL)', place: 'Alwar', person: 'Dinesh Sharma', meta: 'Electricity research page · since 2024' },
      { tier: 'Escalation 3', title: 'Superintending Engineer', place: 'Alwar Circle', person: 'Vijay Singh', meta: 'Electricity research page · 2025' },
      { tier: 'Escalation 4', title: 'Managing Director (JVVNL)', place: 'Jaipur', person: 'Nathmal Didel', meta: 'Electricity research page · 2026' },
      { tier: 'Minister', title: 'Chief Minister (Energy)', place: 'Rajasthan', person: '[Chief Minister]', meta: 'elected · political escalation' },
    ],
  },
  Roads: {
    dept: 'Roads (PWD)',
    steps: [
      { tier: 'Follows up', title: 'Junior Engineer (PWD)', place: 'Reni', status: 'review' },
      { tier: 'Escalation 1', title: 'Assistant Engineer', place: 'Rajgarh', status: 'gap' },
      { tier: 'Escalation 2', title: 'Executive Engineer (PWD)', place: 'Alwar', person: 'Sohan Lal', meta: 'Roads research page · since 2024' },
      { tier: 'Escalation 3', title: 'Superintending Engineer', place: 'Alwar Circle', person: 'Ramesh Chand', meta: 'Roads research page · 2025' },
      { tier: 'Escalation 4', title: 'Chief Engineer (PWD)', place: 'Rajasthan', person: 'D. R. Meghwal', meta: 'Roads research page · 2026' },
      { tier: 'Minister', title: 'Chief Minister (PWD)', place: 'Rajasthan', person: '[Chief Minister]', meta: 'elected · political escalation' },
    ],
  },
};

const areaSize = (scope: string) => (scope === 'all' ? 3 : scope.startsWith('d:') ? 2 : 1);
const covers = (scope: string, v: (typeof VILLAGES)[number]) => {
  if (scope === 'all') return true;
  if (scope.startsWith('d:')) return scope.slice(2) === v.district;
  if (scope.startsWith('t:')) return scope.slice(2) === v.tehsil;
  return false;
};

export default function ClientTeamPage() {
  const navigate = useNavigate();
  const { data: team = [], isLoading: teamLoading, refetch: refetchTeam } =
    useGetClientTeamQuery(undefined, { refetchOnMountOrArgChange: true });
  const { data: roles = [] } = useGetClientRolesQuery();
  const [deleteUser] = useDeleteUserMutation();
  const removeMember = async (id: number) => {
    try {
      await deleteUser(id).unwrap();
      refetchTeam();
    } catch {
      /* surfaced elsewhere */
    }
  };
  const [cat, setCat] = useState(CATS[1]);
  const [village, setVillage] = useState(VILLAGES[0].value);

  const LABEL = 'text-xs font-semibold text-ink-secondary';

  // Roles/permissions matrix from the API: columns = roles, rows = every permission action.
  const permActions = Array.from(new Set(roles.flatMap((r) => r.permissions))).sort();
  const rolesCols = `minmax(200px,1.5fr) repeat(${Math.max(roles.length, 1)}, minmax(90px,1fr))`;

  const v = VILLAGES.find((x) => x.value === village) ?? VILLAGES[0];
  // The "who handles" explorer is illustrative — member area/handles aren't in the API yet.
  const assignees = INITIAL_MEMBERS
    .filter((m) => m.handles.includes(cat) && covers(m.scope, v))
    .sort((a, b) => areaSize(a.scope) - areaSize(b.scope));
  const chain = CHAINS[cat];

  return (
    <div className="flex flex-col gap-[18px]">
      <section className="flex flex-col gap-1.5">
        <span className="text-xs font-semibold text-brand">CLIENT SETUP</span>
        <h1 className="text-2xl font-semibold leading-9 tracking-[-0.75px] text-ink">Team and access</h1>
        <p className="max-w-[780px] text-sm leading-6 text-ink-secondary">
          Each member has a role (what they can do), an area (where), and the kinds of request they
          handle (what). A new request goes to the member with the smallest area that covers it.
        </p>
      </section>

      {/* Roles matrix — from GET /platform/client/roles/ */}
      <section className="flex flex-col gap-2 rounded-xl border border-line/30 bg-white p-5 shadow-card">
        <h2 className="text-lg font-bold text-ink">Roles</h2>
        {roles.length === 0 ? (
          <span className="text-[13px] text-ink-secondary">No roles configured yet.</span>
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-[560px]">
              <div className="grid gap-2 border-b border-[#f3f4f6] py-1.5 text-[11px] text-ink-secondary" style={{ gridTemplateColumns: rolesCols }}>
                <span>PERMISSION</span>
                {roles.map((r) => (
                  <span key={r.id} className="text-center" title={r.description ?? undefined}>{r.name}</span>
                ))}
              </div>
              {permActions.map((action) => (
                <div key={action} className="grid gap-2 border-b border-[#f3f4f6] py-1.5 text-[13px]" style={{ gridTemplateColumns: rolesCols }}>
                  <span className="text-ink">{action}</span>
                  {roles.map((r) => {
                    const on = r.permissions.includes(action);
                    return (
                      <span key={r.id} className={['text-center font-bold', on ? 'text-brand' : 'text-[#9ca3af]'].join(' ')}>
                        {on ? '✓' : '—'}
                      </span>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Members — the org's users (GET /platform/client/team/) with add / edit / delete */}
      <section className="flex flex-col gap-2 rounded-xl border border-line/30 bg-white p-5 shadow-card">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-bold text-ink">Members</h2>
          <button
            type="button"
            onClick={() => navigate('/client/team/new?returnTo=/client/team')}
            className="h-10 rounded-lg bg-brand px-4 text-[13px] font-semibold text-white shadow-btn transition-colors hover:bg-brand-dark"
          >
            + Add member
          </button>
        </div>
        {teamLoading && <span className="text-[13px] text-ink-secondary">Loading members…</span>}
        {!teamLoading && team.length === 0 && (
          <span className="text-[13px] text-ink-secondary">No members in your organization yet.</span>
        )}
        {team.map((m) => (
          <div key={m.id} className="flex flex-wrap items-center justify-between gap-2 border-t border-[#f3f4f6] py-2.5">
            <span className="flex min-w-0 flex-col">
              <b className="text-sm text-ink">{m.name || '—'}</b>
              <span className="text-xs text-ink-secondary">
                {[desigText(m.designation), m.email].filter(Boolean).join(' · ') || 'No designation'}
              </span>
            </span>
            <div className="flex flex-wrap items-center gap-2">
              <span className="flex flex-wrap gap-1.5">
                {m.roles.length > 0 ? (
                  m.roles.map((r) => (
                    <span key={r} className="rounded-full bg-brand-shade px-2.5 py-0.5 text-xs font-semibold text-brand">
                      {r}
                    </span>
                  ))
                ) : (
                  <span className="text-xs text-ink-secondary">No role</span>
                )}
              </span>
              <button
                type="button"
                onClick={() => navigate(`/client/team/${m.id}/edit?returnTo=/client/team`)}
                className="h-9 rounded-lg border border-line bg-white px-3 text-xs font-semibold text-ink transition-colors hover:bg-brand-shade"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => void removeMember(m.id)}
                className="h-9 rounded-lg border border-[#fecaca] bg-white px-3 text-xs font-semibold text-[#dc2626] transition-colors hover:bg-[#fef2f2]"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </section>

      {/* Who handles a request */}
      <section className="flex flex-col gap-2.5 rounded-xl border border-line/30 bg-white p-5 shadow-card">
        <h2 className="text-lg font-bold text-ink">Who handles a request?</h2>
        <span className="text-[13px] text-ink-secondary">
          Try a request type and a village. The officer comes from the department’s hierarchy and the
          platform’s records; the team member comes from the areas above.
        </span>
        <div className="flex flex-wrap gap-2.5">
          <label className="form-field-select flex flex-col gap-1">
            <span className={LABEL}>Request type</span>
            <Select size="large" style={{ minWidth: 220 }} value={cat} onChange={setCat} options={CATS.map((c) => ({ value: c, label: c }))} />
          </label>
          <label className="form-field-select flex flex-col gap-1">
            <span className={LABEL}>Village</span>
            <Select size="large" style={{ minWidth: 220 }} value={village} onChange={setVillage} options={VILLAGES.map((x) => ({ value: x.value, label: x.label }))} />
          </label>
        </div>

        <div className="grid grid-cols-1 gap-x-8 gap-y-4 pt-1 lg:grid-cols-[1.6fr_1fr]">
          {/* Government escalation chain */}
          <div className="flex flex-col">
            {chain ? (
              <>
                <span className="pb-1 text-xs font-semibold uppercase tracking-[0.03em] text-ink-secondary">
                  Government follow-up · {chain.dept}
                </span>
                {chain.steps.map((s, i) => (
                  <div key={i} className="grid grid-cols-[110px_1fr] gap-3 border-t border-[#f3f4f6] py-2.5">
                    <span className="text-xs text-ink-secondary">{s.tier}</span>
                    <span className="flex flex-col gap-0.5">
                      <span className="text-[13px] text-ink">
                        <b>{s.title}</b> · {s.place}
                      </span>
                      {s.person && <span className="text-[13px] font-semibold text-ink">{s.person}</span>}
                      {s.status === 'review' && <span className="text-[13px] font-semibold text-[#b42318]">Submitted, waiting for review</span>}
                      {s.status === 'gap' && <span className="text-[13px] font-semibold text-[#b42318]">No one on record</span>}
                      {s.status ? (
                        <button
                          type="button"
                          onClick={() => navigate('/client/gaps')}
                          className="w-fit text-left text-xs text-ink-secondary underline-offset-2 hover:underline"
                        >
                          gap: add under Key people &amp; gaps
                        </button>
                      ) : (
                        s.meta && <span className="text-xs text-ink-secondary">{s.meta}</span>
                      )}
                    </span>
                  </div>
                ))}
              </>
            ) : (
              <span className="text-[13px] text-ink-secondary">
                Party requests are routed inside the party hierarchy, not through government officers.
              </span>
            )}
          </div>

          {/* The client's own team */}
          <div className="flex flex-col gap-1">
            <span className="pb-1 text-xs font-semibold uppercase tracking-[0.03em] text-ink-secondary">Your team</span>
            {assignees.length === 0 ? (
              <span className="text-[13px] text-[#b42318]">No member handles this type of request in this village. Add one, or widen an area.</span>
            ) : (
              assignees.map((a, i) => (
                <div key={a.id} className="flex flex-col gap-0.5 py-1.5">
                  <span className="flex items-center gap-1.5">
                    <span className={['rounded px-1.5 py-0.5 text-[11px] font-semibold', i === 0 ? 'bg-brand text-white' : 'bg-muted-bg text-[#434655]'].join(' ')}>
                      {i === 0 ? 'Assigned to' : 'Also covers'}
                    </span>
                    <b className="text-[13px] text-ink">{a.name}</b>
                  </span>
                  <span className="text-xs text-ink-secondary">
                    {a.role} · {SCOPE_OPTS.find((s) => s.value === a.scope)?.label ?? a.scope}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
